import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import ThemeContext from '../../contexts/ThemeContext';
import SearchSelect from '../SearchSelect';
import Label from '../Label';
import GlobeIcon from '../../images/GlobeIcon';
import { Section } from '../PrebookingSharedComponents';
import { ErrorText } from './shared';
import i18n from '../../i18n';
import { getTranslationOrderLanguages } from '../../utils/languageUtils';

const LanguageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const LanguageSelectWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

/* Label height (~14.4px) + label margin-bottom (8px) ≈ 22px, so the arrow
   centers vertically with the input even when one column has an error
   message below it. */
const ArrowSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $colors }) => $colors.twilight};
  font-size: ${({ $layout }) => $layout.fontSize}px;
  height: 40px;
  margin-top: 22px;

  @media (max-width: 768px) {
    height: auto;
    margin-top: 0;
    transform: rotate(90deg);
  }
`;

const LanguageSelection = ({ formData, updateField, errors, clearError }) => {
  const { colors, layout } = useContext(ThemeContext);
  // Selectable languages + target languages: a translation can go in either
  // direction, so both dropdowns offer the union of the two lists.
  const languages = getTranslationOrderLanguages(formData.selectableLanguages, formData.targetLanguages);
  const fromLanguage = languages.find(l => l.id === formData.fromLanguageId);
  const toLanguage = languages.find(l => l.id === formData.toLanguageId);

  const languageOptions = languages.map(lang => ({
    value: lang.id,
    label: lang.name,
  }));

  return (
    <Section>
      <LanguageRow>
        <LanguageSelectWrapper>
          <Label text={i18n('translation_order_language_from')} required />
          <SearchSelect
            selected={fromLanguage ? { value: fromLanguage.id, label: fromLanguage.name } : null}
            onChange={(option) => {
              updateField('fromLanguageId', option?.value || null);
              clearError('fromLanguageId');
            }}
            selectableItems={languageOptions}
            placeholder={i18n('select')}
            customHeight="40px"
            customBackgroundColor={colors.webLightGray}
            customBorderColor={colors.webDefaultInputBorderColor}
            icon={<GlobeIcon color={colors.iconAccentColor} size={20} />}
          />
          {errors.fromLanguageId && <ErrorText>{i18n(errors.fromLanguageId)}</ErrorText>}
        </LanguageSelectWrapper>
        <ArrowSeparator $colors={colors} $layout={layout} aria-hidden="true">&gt;</ArrowSeparator>
        <LanguageSelectWrapper>
          <Label text={i18n('translation_order_language_to')} required />
          <SearchSelect
            selected={toLanguage ? { value: toLanguage.id, label: toLanguage.name } : null}
            onChange={(option) => {
              updateField('toLanguageId', option?.value || null);
              clearError('toLanguageId');
            }}
            selectableItems={languageOptions}
            placeholder={i18n('select')}
            customHeight="40px"
            customBackgroundColor={colors.webLightGray}
            customBorderColor={colors.webDefaultInputBorderColor}
            icon={<GlobeIcon color={colors.iconAccentColor} size={20} />}
          />
          {errors.toLanguageId && <ErrorText>{i18n(errors.toLanguageId)}</ErrorText>}
        </LanguageSelectWrapper>
      </LanguageRow>
    </Section>
  );
};

LanguageSelection.propTypes = {
  formData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  clearError: PropTypes.func.isRequired,
};

export default LanguageSelection;
